import { Link, router } from 'expo-router';
import * as React from 'react';
import {
    BodyMediumEmphasized,
    BodySmallRegular,
    Button,
    ButtonSize,
    ButtonVariant,
    FullScreenSpinner,
    Image,
    LabelRegular,
    ScreenContainer,
    Shadows,
    View,
    XStack,
    YStack,
} from 'smoothie';

import imChart from '../../../../assets/images/investing/invest-splash-chart.png';
import imData from '../../../../assets/images/investing/invest-splash-data.png';
import GuideAvatars from '../../../onboarding/components/GuideAvatars';
import { useMoneyMapWorkflow } from '../../hooks/useMoneyMapWorkflow';
import { GoWorkflowStepType } from '../../types/GoWorkflow';
import { GoWorkflowHeader } from '../GoWorkflowHeader';

export const CmmImInfo = () => {
    const {
        isLoadingCompleteness,
        moneyMapCompleteness,
        getNextStep,
        proceedForward,
    } = useMoneyMapWorkflow();

    const { cardAccountsStep, cashAccountsStep } = moneyMapCompleteness;
    const nextStep = getNextStep(GoWorkflowStepType.CMM_IM);

    const isNextStepIncomeSplit =
        nextStep?.type === GoWorkflowStepType.INCOME_SPLIT;

    const isAwaitingPartnerActions =
        (cashAccountsStep.isIncluded &&
            !cashAccountsStep.isCompleteForMembership) ||
        (cardAccountsStep.isIncluded &&
            !cardAccountsStep.isCompleteForMembership);

    if (isLoadingCompleteness) {
        return <FullScreenSpinner />;
    }

    return (
        <>
            <GoWorkflowHeader
                stepType={GoWorkflowStepType.CMM_IM}
                title="Investment accounts"
            />
            <ScreenContainer useSafeAreaInsetTop={false}>
                <YStack justifyContent="space-between" gap={24} width="100%">
                    <YStack
                        gap={4}
                        padding={16}
                        backgroundColor="$gray100"
                        borderRadius={24}
                        width="100%"
                    >
                        <GuideAvatars size="medium" />
                        <BodyMediumEmphasized>
                            After you activate your system
                        </BodyMediumEmphasized>
                        <BodySmallRegular color="$gray900">
                            Book a complimentary session with one of our Guides
                            to open investment accounts.
                        </BodySmallRegular>
                    </YStack>
                    <YStack
                        paddingVertical={12}
                        width="100%"
                        pointerEvents="none"
                    >
                        <XStack
                            width="100%"
                            justifyContent="center"
                            alignItems="flex-start"
                        >
                            <View
                                transform={[
                                    { translateX: 90 },
                                    { translateY: 20 },
                                    { rotate: '9deg' },
                                ]}
                                {...Shadows.LARGE}
                            >
                                <Image
                                    source={imData}
                                    height={185}
                                    width={234}
                                    objectFit="contain"
                                    resizeMode="contain"
                                />
                            </View>
                            <View
                                transform={[
                                    { translateX: -160 },
                                    { rotate: '-4deg' },
                                ]}
                                {...Shadows.LARGE}
                            >
                                <Image
                                    source={imChart}
                                    height={238}
                                    width={175}
                                    objectFit="contain"
                                    resizeMode="contain"
                                />
                            </View>
                        </XStack>
                    </YStack>
                    <LabelRegular color="$gray900">
                        Images shown for illustrative purposes only. All
                        investing involves risk, including the risk of losing
                        the money you invest, and past performance does not
                        guarantee future performance.
                    </LabelRegular>
                    <Button
                        variant={ButtonVariant.PRIMARY}
                        text="Got it"
                        width="full"
                        size={ButtonSize.LARGE}
                        onPress={() => {
                            isNextStepIncomeSplit && !isAwaitingPartnerActions
                                ? proceedForward(GoWorkflowStepType.CMM_IM)
                                : router.navigate('/home/roadmap');
                        }}
                    />
                    <LabelRegular color="$gray900">
                        Investment advice offered by Fruitful Advisory, LLC, a
                        registered investment adviser. Additional disclosures
                        can be referenced at{' '}
                        <Link
                            href="https://www.fruitful.com/legal"
                            target="_blank"
                        >
                            <LabelRegular
                                textDecorationLine="underline"
                                color="$gray900"
                            >
                                fruitful.com/legal
                            </LabelRegular>
                        </Link>
                        .
                    </LabelRegular>
                </YStack>
            </ScreenContainer>
        </>
    );
};
